import { useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ChevronDownIcon, ChevronRightIcon, XIcon } from "lucide-react";

import { DebouncedInput } from "~/components/debounced-input";
import { Field, FilterSelect } from "~/components/filter-controls";
import { PageHeader } from "~/components/page-header";
import { PaginationBar } from "~/components/pagination-bar";
import { TableEmpty, TableError, TableSkeleton } from "~/components/table-states";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { errorMessage } from "~/lib/api";
import { formatDateTime, formatRelative } from "~/lib/format";
import { useAuditLog, type AuditLogEntry } from "~/lib/queries";
import { AuditLogDiff } from "./-diff";
import {
  hasActiveFilters,
  toFilters,
  validateAuditLogSearch,
  type AuditLogSearch,
} from "./-search";

export const Route = createFileRoute("/_authed/audit-log/")({
  validateSearch: validateAuditLogSearch,
  component: AuditLogPage,
});

const COLUMNS = 5;

/** Every action recorded so far. Kept in sync with models/audit_log.go by
 *  hand -- there is no shared source of truth across the two languages, and a
 *  new action showing up as raw dotted text in the table is a safe failure,
 *  not a broken one. */
const ACTION_OPTIONS = [
  { value: "storefront.create", label: "Storefront created" },
  { value: "storefront.update", label: "Storefront updated" },
  { value: "callback_config.set", label: "Callback URL set" },
  { value: "callback_config.clear", label: "Callback config cleared" },
  { value: "callback_config.rotate_secret", label: "Callback secret rotated" },
  { value: "callback_delivery.replay", label: "Callback delivery replayed" },
  { value: "webhook_event.retry", label: "Webhook event retried" },
  { value: "payment_conflict.accept", label: "Late payment accepted" },
  { value: "payment_conflict.mark_refunded", label: "Late payment marked refunded" },
  { value: "api_key.create", label: "API key minted" },
  { value: "api_key.revoke", label: "API key revoked" },
  { value: "provider_status.check", label: "Provider status checked" },
  { value: "shopify_credentials.set", label: "Shopify credentials set" },
  { value: "shopify_credentials.remove", label: "Shopify credentials removed" },
  { value: "shopify_oauth_credentials.set", label: "Shopify OAuth credentials set" },
  { value: "shopify_oauth_credentials.remove", label: "Shopify OAuth credentials removed" },
];

const TARGET_TYPE_OPTIONS = [
  { value: "storefront", label: "Storefront" },
  { value: "transaction", label: "Transaction" },
  { value: "api_key", label: "API key" },
  { value: "callback_config", label: "Callback config" },
  { value: "callback_delivery", label: "Callback delivery" },
  { value: "webhook_event", label: "Webhook event" },
  { value: "payment_conflict", label: "Payment conflict" },
];

/**
 * Every admin mutation, findable by actor, action or target.
 *
 * The only real control on a surface with no RBAC: every staff member can do
 * everything here, so this screen — not a permission check — is what answers
 * "who did this, and what did it look like before". Append-only by design,
 * and said so on the page: there is no edit, delete or bulk action anywhere
 * here, which is most of the point of a log like this.
 *
 * Rows expand in place rather than opening a detail route: a row is already
 * small by the time it is redacted server-side, so a second round trip per
 * click would buy nothing.
 */
function AuditLogPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filters = toFilters(search);
  const { data, error, isPending, isFetching, refetch } = useAuditLog(filters);

  function patch(next: Partial<AuditLogSearch>) {
    void navigate({
      search: (previous) => ({
        ...previous,
        ...next,
        offset: "offset" in next ? next.offset : 0,
      }),
      replace: true,
    });
  }

  function clearFilters() {
    void navigate({ search: (previous) => ({ limit: previous.limit }), replace: true });
  }

  const rows = data?.items ?? [];
  const filtered = hasActiveFilters(search);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Audit log"
        description="Every admin mutation, who made it and what it changed. Append-only — there is no edit, delete or bulk action on this screen."
      />

      <FilterBar search={search} onChange={patch} onClear={clearFilters} hasFilters={filtered} />

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-8" />
              <TableHead>When</TableHead>
              <TableHead>Who</TableHead>
              <TableHead>What</TableHead>
              <TableHead>Target</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {isPending ? (
              <TableSkeleton columns={COLUMNS} />
            ) : error ? (
              <TableError
                columns={COLUMNS}
                message={errorMessage(error)}
                onRetry={() => void refetch()}
              />
            ) : rows.length === 0 ? (
              <TableEmpty
                columns={COLUMNS}
                message={
                  filtered
                    ? "No audit rows match these filters."
                    : "No admin mutations recorded yet."
                }
                onClear={filtered ? clearFilters : undefined}
              />
            ) : (
              rows.map((row) => (
                <AuditRow
                  key={row.id}
                  row={row}
                  expanded={expandedId === row.id}
                  onToggle={() => setExpandedId(expandedId === row.id ? null : row.id)}
                  onFilterActor={() => patch({ actor_id: row.actor_id })}
                />
              ))
            )}
          </TableBody>
        </Table>

        {data ? (
          <PaginationBar
            pagination={data.pagination}
            onChange={(next) => patch(next)}
            disabled={isFetching}
          />
        ) : null}
      </div>
    </div>
  );
}

function AuditRow({
  row,
  expanded,
  onToggle,
  onFilterActor,
}: {
  row: AuditLogEntry;
  expanded: boolean;
  onToggle: () => void;
  onFilterActor: () => void;
}) {
  return (
    <>
      <TableRow className="cursor-pointer" onClick={onToggle}>
        <TableCell>
          {expanded ? (
            <ChevronDownIcon className="size-3.5 text-muted-foreground" />
          ) : (
            <ChevronRightIcon className="size-3.5 text-muted-foreground" />
          )}
        </TableCell>
        <TableCell className="text-muted-foreground" title={formatDateTime(row.created_at)}>
          {formatRelative(row.created_at)}
        </TableCell>
        <TableCell>
          {/* Click-to-filter rather than a dropdown: there is no staff-list
              endpoint to populate one from, and every actor already appears in
              the rows they wrote. */}
          {row.actor_id ? (
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={(event) => {
                event.stopPropagation();
                onFilterActor();
              }}
            >
              {row.actor_email}
            </button>
          ) : (
            row.actor_email
          )}
        </TableCell>
        <TableCell className="font-mono text-[0.8125rem]">{row.action}</TableCell>
        <TableCell>
          <TargetCell row={row} />
        </TableCell>
      </TableRow>
      {expanded ? (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={COLUMNS} className="bg-muted/30">
            <AuditLogDiff before={row.before} after={row.after} />
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

function TargetCell({ row }: { row: AuditLogEntry }) {
  if (row.target_type === "storefront") {
    return (
      <Link
        to="/storefronts/$storefrontId"
        params={{ storefrontId: row.target_id }}
        className="font-mono text-[0.8125rem] text-primary hover:underline"
        onClick={(event) => event.stopPropagation()}
      >
        {row.target_type}:{row.target_id}
      </Link>
    );
  }
  return (
    <span className="font-mono text-[0.8125rem] text-muted-foreground">
      {row.target_type}:{row.target_id}
    </span>
  );
}

function FilterBar({
  search,
  onChange,
  onClear,
  hasFilters,
}: {
  search: AuditLogSearch;
  onChange: (patch: Partial<AuditLogSearch>) => void;
  onClear: () => void;
  hasFilters: boolean;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3">
      <Field label="Action">
        <FilterSelect
          value={search.action ?? null}
          onValueChange={(value) => onChange({ action: value ?? undefined })}
          options={ACTION_OPTIONS}
          placeholder="Any action"
          className="w-56"
        />
      </Field>

      <Field label="Target type">
        <FilterSelect
          value={search.target_type ?? null}
          onValueChange={(value) => onChange({ target_type: value ?? undefined })}
          options={TARGET_TYPE_OPTIONS}
          placeholder="Any target"
          className="w-44"
        />
      </Field>

      <Field label="Target ID">
        <DebouncedInput
          value={search.target_id ?? ""}
          onCommit={(value) => onChange({ target_id: value || undefined })}
          placeholder="Exact match"
          className="w-56"
        />
      </Field>

      <Field label="From">
        <Input
          type="date"
          value={search.start_date ?? ""}
          onChange={(event) => onChange({ start_date: event.target.value || undefined })}
          className="w-[8.5rem]"
        />
      </Field>

      <Field label="To">
        <Input
          type="date"
          value={search.end_date ?? ""}
          onChange={(event) => onChange({ end_date: event.target.value || undefined })}
          className="w-[8.5rem]"
        />
      </Field>

      {search.actor_id ? (
        <Field label="Actor">
          <div className="flex h-8 items-center gap-1.5 rounded-lg border bg-muted/40 px-2.5 font-mono text-xs text-muted-foreground">
            {search.actor_id}
            <button
              type="button"
              aria-label="Clear actor filter"
              onClick={() => onChange({ actor_id: undefined })}
            >
              <XIcon className="size-3" />
            </button>
          </div>
        </Field>
      ) : null}

      {hasFilters ? (
        <Button variant="ghost" size="sm" onClick={onClear} className="ml-auto">
          <XIcon className="size-3.5" />
          Clear
        </Button>
      ) : null}
    </div>
  );
}
